import { useLocation, useNavigate } from 'react-router-dom';
import { LockIcon, LogOutIcon } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { cn } from '../../utils/cn';
import { useAuth } from '../../hooks/useAuth';
import { useAuthStore } from '../../stores/authStore';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { useBrandingStore } from '../../stores/brandingStore';
import { NAV_SECTIONS } from '../../constants/navConfig';
import { ROUTES } from '../../constants/routes';

interface MobileNavProps {
  open: boolean;
  onClose: () => void;
}

export default function MobileNav({ open, onClose }: MobileNavProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { can } = useAuth();

  const user = useAuthStore((s) => s.user);
  const company = useAuthStore((s) => s.company);
  const role = useAuthStore((s) => s.role);
  const clearAuth = useAuthStore((s) => s.clearAuth);

  const hasModule = useSubscriptionStore((s) => s.hasModule);
  const logoUrl = useBrandingStore((s) => s.config?.logo_url);

  function handleNav(href: string) {
    onClose();
    navigate(href);
  }

  function handleSignOut() {
    onClose();
    clearAuth();
    queryClient.clear();
    navigate(ROUTES.LOGIN, { replace: true });
  }

  const roleLabel: Record<string, string> = {
    'ic-admin': 'Admin',
    'ic-underwriter': 'Underwriter',
    'ic-claims-officer': 'Claims Officer',
    'ic-accountant': 'Accountant',
    'ic-executive': 'Executive',
    'ic-hr-manager': 'HR Manager',
    'super-admin': 'Super Admin',
    admin: 'Admin',
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="left" className="flex flex-col w-72 p-0 bg-sidebar text-white">
        {/* Header */}
        <SheetHeader className="flex h-16 flex-row items-center gap-3 px-4 border-b border-white/10">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="h-8 w-auto object-contain" />
          ) : (
            <SheetTitle className="text-base font-bold text-white">Fast Policy</SheetTitle>
          )}
          {company?.name && (
            <span className="truncate text-xs text-white/60">{company.name}</span>
          )}
        </SheetHeader>

        {/* Nav sections */}
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {NAV_SECTIONS.map(({ section, items }) => {
            const renderedItems = items.filter((item) => {
              if (!role) return false;
              if (item.permission !== null && !can(item.permission)) return false;
              if (item.brandingOnly && !company?.is_white_label) return false;
              return true;
            });

            if (renderedItems.length === 0) return null;

            return (
              <div key={section} className="mb-4">
                <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-white/40">
                  {section}
                </p>

                {renderedItems.map((item) => {
                  const isLocked = item.moduleId !== null && !hasModule(item.moduleId);
                  const isActive = pathname.startsWith(item.href);

                  if (isLocked) {
                    return (
                      <button
                        key={item.href}
                        type="button"
                        className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm opacity-50 cursor-not-allowed"
                        onClick={onClose}
                      >
                        <item.icon className="h-4 w-4 shrink-0" />
                        <span className="flex-1 text-left truncate">{item.label}</span>
                        <LockIcon className="h-3 w-3 shrink-0" />
                      </button>
                    );
                  }

                  return (
                    <button
                      key={item.href}
                      type="button"
                      onClick={() => handleNav(item.href)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                        isActive
                          ? 'bg-white/15 text-white font-medium'
                          : 'text-white/70 hover:bg-white/10 hover:text-white',
                      )}
                    >
                      <item.icon className="h-4 w-4 shrink-0" />
                      <span className="flex-1 text-left truncate">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Footer: user info + sign out */}
        <div className="border-t border-white/10 p-4 space-y-3">
          {user && (
            <div>
              <p className="text-sm font-medium truncate">{user.full_name}</p>
              <p className="text-xs text-white/50 truncate">
                {role ? roleLabel[role] ?? role : ''}
              </p>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10 px-2"
            onClick={handleSignOut}
          >
            <LogOutIcon className="mr-2 h-4 w-4" />
            Sign Out
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
